import { TrendingDown, LineChart, Globe2, Factory, Leaf, ArrowRight, BarChart3, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

const Emissions = () => {
  const emissionsServices = [
    {
      icon: <LineChart className="w-8 h-8 text-blue-700" />,
      title: 'Emissions Monitoring',
      description: 'Real-time tracking and analysis of carbon emissions across global energy operations and supply chains'
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-blue-700" />,
      title: 'Carbon Markets Intelligence',
      description: 'Comprehensive coverage of carbon pricing mechanisms, trading volumes, and regulatory developments'
    },
    {
      icon: <Target className="w-8 h-8 text-blue-700" />,
      title: 'Net-Zero Pathways',
      description: 'Strategic analysis of decarbonization scenarios, technology adoption, and transition economics'
    },
    {
      icon: <Factory className="w-8 h-8 text-blue-700" />,
      title: 'Sector Analysis',
      description: 'Detailed emissions profiles by energy sector including oil & gas, power generation, and industrial facilities'
    }
  ];

  const trackingCapabilities = [
    {
      title: 'Scope 1, 2 & 3 Emissions',
      description: 'Comprehensive tracking across direct emissions, purchased energy, and value chain activities'
    },
    {
      title: 'Carbon Intensity Metrics',
      description: 'Benchmarking and analysis of emissions per unit of energy production across technologies and regions'
    },
    {
      title: 'Regulatory Compliance',
      description: 'Monitoring of emissions reporting requirements, carbon border adjustments, and climate policy mandates'
    },
    {
      title: 'Abatement Technologies',
      description: 'Assessment of carbon capture, hydrogen, and other emissions reduction technologies and their economics'
    }
  ];

  const carbonMarkets = [
    {
      name: 'EU ETS',
      description: 'European Union Emissions Trading System',
      coverage: ['Carbon allowances', 'Compliance markets', 'Price forecasts']
    },
    {
      name: 'US Regional Markets',
      description: 'RGGI, California Cap-and-Trade, and emerging programs',
      coverage: ['State policies', 'Trading volumes', 'Price dynamics']
    },
    {
      name: 'Voluntary Carbon Markets',
      description: 'Project-based offsets and corporate commitments',
      coverage: ['Credit pricing', 'Project types', 'Quality standards']
    },
    {
      name: 'Global Carbon Pricing',
      description: 'International mechanisms and bilateral agreements',
      coverage: ['Carbon taxes', 'Border adjustments', 'Policy trends']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Emissions Tracking & Carbon Markets</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Intelligence on energy sector emissions, carbon pricing mechanisms, and decarbonization pathways for climate strategy.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <Leaf className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-slate-900 mb-1">50+</div>
              <div className="text-sm text-slate-600">Countries Tracked</div>
            </div>
            <div className="text-center">
              <Factory className="w-8 h-8 text-blue-700 mx-auto mb-3" />
              <div className="text-3xl font-bold text-slate-900 mb-1">1000+</div>
              <div className="text-sm text-slate-600">Facilities Monitored</div>
            </div>
            <div className="text-center">
              <TrendingDown className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-slate-900 mb-1">10+</div>
              <div className="text-sm text-slate-600">Carbon Markets</div>
            </div>
            <div className="text-center">
              <Globe2 className="w-8 h-8 text-blue-700 mx-auto mb-3" />
              <div className="text-3xl font-bold text-slate-900 mb-1">Real-Time</div>
              <div className="text-sm text-slate-600">Data Updates</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Our Emissions Intelligence Services
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Actionable insights to navigate the evolving landscape of climate policy and carbon markets
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {emissionsServices.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tracking Capabilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              What We Track
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive emissions data and analysis across the energy value chain
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {trackingCapabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-slate-50 p-8 rounded-lg border border-slate-200"
              >
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {capability.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Carbon Markets Coverage */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Carbon Markets Coverage
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Intelligence on compliance and voluntary carbon markets worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {carbonMarkets.map((market, index) => (
              <div
                key={index}
                className="bg-white rounded-lg border-2 border-slate-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-6 border-b border-slate-200">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {market.name}
                  </h3>
                  <p className="text-slate-600">{market.description}</p>
                </div>
                <div className="p-6">
                  <div className="text-sm font-semibold text-slate-700 mb-3">
                    Coverage Areas:
                  </div>
                  <ul className="space-y-2">
                    {market.coverage.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-center space-x-2 text-sm text-slate-600"
                      >
                        <div className="w-1.5 h-1.5 bg-blue-700 rounded-full"></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Intelligence Deliverables
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our emissions tracking service provides regular updates and strategic analysis to support your climate commitments and carbon market participation
              </p>
              <ul className="space-y-4">
                {[
                  'Monthly emissions tracking reports by region and sector',
                  'Weekly carbon market commentary and price forecasts',
                  'Quarterly policy and regulatory impact assessments',
                  'Real-time alerts on significant climate policy developments',
                  'Annual decarbonization pathway scenarios and modeling',
                  'Custom emissions analysis and climate strategy consulting'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-green-700 rounded-full"></div>
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-green-50 p-8 rounded-lg border-2 border-blue-200 shadow-xl">
              <TrendingDown className="w-12 h-12 text-green-700 mb-6" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Net-Zero Transition Intelligence
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our proprietary models assess decarbonization pathways and economics including:
              </p>
              <div className="space-y-3">
                {[
                  'Technology adoption curves and cost trajectories',
                  'Carbon abatement cost curves by sector and region',
                  'Investment requirements for net-zero scenarios',
                  'Policy risk assessment and carbon price forecasts',
                  'Stranded asset analysis and transition risks'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-md">
                    <div className="w-1.5 h-1.5 bg-green-700 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#157D3F] via-[#195C63] to-[#1D3B87]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Leaf className="w-16 h-16 text-white/80 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-white mb-6">
            Navigate the Energy Transition With Confidence
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Access comprehensive emissions intelligence and carbon market analysis to support your climate strategy
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#1D3B87] font-semibold rounded-md hover:bg-slate-100 transition-all duration-200 shadow-lg"
          >
            Request Emissions Brief
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Emissions;
